import { useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  CHAT_DEFAULT_LIVE_BAR_FETCH_PARAMS,
  MIN_ALLOWED_LIVE_BAR_POLL_PERIOD_MS,
} from "chat/constants";
import { RootState } from "chat/imports/state";
import { fetchLiveBarRequest } from "chat/liveBar/state/asyncAction";
import { liveBarSelectors } from "chat/liveBar/state/selectors";
import { resetLiveBar } from "chat/liveBar/state/slice";
import { GetLiveBarRequestsParams } from "chat/liveBar/types";
import {
  getChatLiveBarModerationLevel,
  getIsChatLiveBarEnabled,
} from "chat/soc/chatSoc";
import { getChatLiveBarPollPeriodMs } from "environment";

const selector = (state: RootState) => ({
  isChatLiveBarEnabled: getIsChatLiveBarEnabled(state),
  moderationLevel: getChatLiveBarModerationLevel(state),
  isInitialized: liveBarSelectors.getIsInitialized(state),
  isLoading: liveBarSelectors.getLoading(state),
  lastTimestamp: liveBarSelectors.getLastTimestamp(state),
});

const useLiveBar = () => {
  const dispatch = useDispatch();

  const {
    isChatLiveBarEnabled,
    moderationLevel,
    isInitialized,
    isLoading,
    lastTimestamp,
  } = useSelector(selector, shallowEqual);

  const fetchLiveBar = useCallback(
    (args?: GetLiveBarRequestsParams) => {
      dispatch(
        fetchLiveBarRequest({
          ...CHAT_DEFAULT_LIVE_BAR_FETCH_PARAMS,
          refresh: true,
          moderation_level: moderationLevel,
          ...args,
        })
      );
    },
    [dispatch, moderationLevel]
  );

  useEffect(() => {
    if (!isChatLiveBarEnabled || isLoading) {
      return;
    }

    if (!isInitialized) {
      fetchLiveBar();

      return;
    }

    const chatLiveBarPollPeriodMs = getChatLiveBarPollPeriodMs();

    if (chatLiveBarPollPeriodMs < MIN_ALLOWED_LIVE_BAR_POLL_PERIOD_MS) {
      return;
    }

    const intervalHandle = setInterval(() => {
      fetchLiveBar({
        last_timestamp:
          lastTimestamp && lastTimestamp !== "0" ? lastTimestamp : Date.now(),
      });
    }, chatLiveBarPollPeriodMs);

    return () => {
      clearInterval(intervalHandle);
    };
  }, [
    isLoading,
    fetchLiveBar,
    isInitialized,
    lastTimestamp,
    dispatch,
    isChatLiveBarEnabled,
  ]);

  useEffect(
    () => () => {
      dispatch(resetLiveBar());
    },
    [dispatch]
  );
};

export default useLiveBar;
